import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
    DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { User, LogOut, Briefcase, ShieldCheck, Calendar, Building2, Coins, Sparkle, Menu, X } from "lucide-react";
import ServicesMenuBar from "@/components/ServicesMenuBar";
import {
    Sheet,
    SheetContent,
    SheetTrigger,
    SheetTitle,
    SheetClose,
} from "@/components/ui/sheet";

const LOGO_URL = "/smart-logo.jpeg";

export default function Header() {
    const { user, logout } = useAuth();
    const navigate = useNavigate();

    return (
        <header
            className="sticky top-0 z-50 glass border-b border-white/5"
            data-testid="site-header"
        >
            <div className="max-w-7xl mx-auto px-6 lg:px-10 h-20 flex items-center justify-between">
                {/* Mobile hamburger — prominent gold on black */}
                <Sheet>
                    <SheetTrigger asChild>
                        <button
                            type="button"
                            aria-label="Open menu"
                            data-testid="mobile-menu-trigger"
                            className="md:hidden mr-3 grid place-items-center w-11 h-11 rounded-md border border-[#FFD700]/45 bg-black/60 text-[#FFD700] hover:bg-[#FFD700]/10 active:scale-95 transition-all shadow-[0_0_18px_-6px_rgba(255,215,0,0.7)]"
                        >
                            <Menu size={22} strokeWidth={2.2} />
                        </button>
                    </SheetTrigger>
                    <SheetContent
                        side="left"
                        className="bg-black border-r border-[#FFD700]/30 text-foreground w-72 p-0"
                        data-testid="mobile-menu-panel"
                    >
                        <SheetTitle className="sr-only">SMART navigation</SheetTitle>
                        <div className="p-6 border-b border-white/10">
                            <div className="text-[9px] tracking-[0.32em] uppercase text-amber-300/80">SMART DEMANDS 24/7</div>
                            <div className="font-serif text-2xl mt-1 gold-text">Navigate</div>
                        </div>
                        <nav className="flex flex-col p-2" data-testid="mobile-menu-nav">
                            {[
                                { to: "/home", label: "Home" },
                                { to: "/join", label: "Join SMART" },
                                { to: "/services", label: "Services / Store" },
                                { to: "/shipping", label: "Shipping" },
                                { to: "/vehicles", label: "Wheels · Vehicles" },
                                { to: "/vip", label: "VIP" },
                                { to: "/become-provider", label: "Become a Pro" },
                                { to: "/wheels/apply", label: "Drive with SMART" },
                                { to: "/venue/register", label: "Venue Partner" },
                                { to: user ? "/my-bookings" : "/auth", label: user ? "My Bookings" : "Sign in" },
                            ].map((item) => (
                                <SheetClose asChild key={item.to}>
                                    <Link
                                        to={item.to}
                                        data-testid={`mobile-menu-link-${item.to.replace(/[/]/g, "-").replace(/^-/, "")}`}
                                        className="px-4 py-3 rounded-md text-sm uppercase tracking-[0.14em] text-muted-foreground hover:text-[#FFD700] hover:bg-[#FFD700]/5 transition-colors"
                                    >
                                        {item.label}
                                    </Link>
                                </SheetClose>
                            ))}
                        </nav>
                        <div className="mt-auto p-6 border-t border-white/10 text-[10px] tracking-[0.25em] uppercase text-amber-300/70">
                            © SMART DEMANDS 24/7 LIMITED
                        </div>
                    </SheetContent>
                </Sheet>

                <Link
                    to="/home"
                    className="flex items-center gap-3 group"
                    data-testid="brand-link"
                >
                    <img
                        src={LOGO_URL}
                        alt="SMART DEMANDS 24/7 LIMITED"
                        className="h-12 w-12 object-contain group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="leading-tight">
                        <span className="font-serif text-xl tracking-[0.15em] block">SMART</span>
                        <span
                            className="hidden sm:block text-[8px] tracking-[0.28em] uppercase text-amber-300/80 -mt-0.5"
                            data-testid="header-llc-name"
                        >
                            DEMANDS 24/7 LIMITED
                        </span>
                    </div>
                </Link>

                <nav className="hidden md:flex items-center gap-10 text-sm">
                    <Link
                        to="/services"
                        className="text-muted-foreground hover:text-foreground transition-colors"
                        data-testid="nav-services"
                    >
                        Services
                    </Link>
                    <Link
                        to="/vip"
                        className="text-muted-foreground hover:text-foreground transition-colors"
                        data-testid="nav-vip"
                    >
                        VIP
                    </Link>
                    <Link
                        to="/vehicles"
                        className="text-muted-foreground hover:text-foreground transition-colors"
                        data-testid="nav-vehicles"
                    >
                        Wheels
                    </Link>
                    <Link
                        to="/marketplace"
                        className="text-muted-foreground hover:text-foreground transition-colors"
                        data-testid="nav-marketplace"
                    >
                        Marketplace
                    </Link>
                    <Link
                        to="/send-home"
                        className="text-muted-foreground hover:text-foreground transition-colors"
                        data-testid="nav-send-home"
                    >
                        Send Home
                    </Link>
                    <Link
                        to="/become-provider"
                        className="text-muted-foreground hover:text-foreground transition-colors"
                        data-testid="nav-provider"
                    >
                        Become a Pro
                    </Link>
                    <Link
                        to="/wheels/apply"
                        className="text-muted-foreground hover:text-foreground transition-colors"
                        data-testid="nav-wheels-apply"
                    >
                        Drive
                    </Link>
                    <Link
                        to="/shipping"
                        className="text-muted-foreground hover:text-foreground transition-colors"
                        data-testid="nav-shipping"
                    >
                        Shipping
                    </Link>
                    <Link
                        to="/venue/register"
                        className="text-muted-foreground hover:text-foreground transition-colors"
                        data-testid="nav-venue"
                    >
                        Venue Partners
                    </Link>
                </nav>

                <div className="flex items-center gap-3">
                    {user && (user.smart_coins || 0) > 0 && (
                        <Link
                            to="/my-bookings"
                            data-testid="header-coin-pill"
                            className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-amber-700/40 bg-amber-900/15 hover:bg-amber-900/30 transition-colors"
                            title={`${(user.smart_coins || 0).toLocaleString()} SMART Coins (1,000 = $10 JMD)`}
                        >
                            <Coins size={13} className="gold-text" strokeWidth={1.5} />
                            <span className="text-xs font-semibold gold-text tabular-nums" data-testid="header-coin-balance">
                                {(user.smart_coins || 0).toLocaleString()}
                            </span>
                        </Link>
                    )}
                    {user ? (
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <Button
                                    variant="outline"
                                    className="border-white/10 bg-secondary/40 hover:bg-secondary text-foreground"
                                    data-testid="user-menu-trigger"
                                >
                                    <User size={16} className="mr-2" strokeWidth={1.5} />
                                    {user.name?.split(" ")[0] || "Account"}
                                </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent
                                align="end"
                                className="bg-card border-white/10 w-56"
                                data-testid="user-menu-content"
                            >
                                <div className="px-3 py-2 text-xs">
                                    <div className="text-muted-foreground">Signed in as</div>
                                    <div className="text-foreground truncate">{user.email}</div>
                                </div>
                                <DropdownMenuSeparator className="bg-white/10" />
                                <DropdownMenuItem
                                    onClick={() => navigate("/my-bookings")}
                                    className="cursor-pointer"
                                    data-testid="menu-my-bookings"
                                >
                                    <Calendar size={14} className="mr-2" strokeWidth={1.5} />
                                    My Bookings
                                </DropdownMenuItem>
                                {(user.role === "provider" || user.role === "admin") && (
                                    <DropdownMenuItem
                                        onClick={() => navigate("/provider")}
                                        className="cursor-pointer"
                                        data-testid="menu-provider"
                                    >
                                        <Briefcase size={14} className="mr-2" strokeWidth={1.5} />
                                        Provider Console
                                    </DropdownMenuItem>
                                )}
                                {user.is_venue_partner && (
                                    <DropdownMenuItem
                                        onClick={() => navigate("/venue/dashboard")}
                                        className="cursor-pointer"
                                        data-testid="menu-venue"
                                    >
                                        <Building2 size={14} className="mr-2" strokeWidth={1.5} />
                                        Venue Console
                                    </DropdownMenuItem>
                                )}
                                {user.role === "admin" && (
                                    <DropdownMenuItem
                                        onClick={() => navigate("/admin")}
                                        className="cursor-pointer"
                                        data-testid="menu-admin"
                                    >
                                        <ShieldCheck size={14} className="mr-2" strokeWidth={1.5} />
                                        Admin Console
                                    </DropdownMenuItem>
                                )}
                                <DropdownMenuSeparator className="bg-white/10" />
                                <DropdownMenuItem
                                    asChild
                                    className="cursor-pointer"
                                    data-testid="menu-royal-ai"
                                >
                                    <a
                                        href="https://knight-hire.preview.emergentagent.com/"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="flex items-center"
                                    >
                                        <Sparkle size={14} className="mr-2" strokeWidth={1.5} />
                                        SMART Royal AI
                                    </a>
                                </DropdownMenuItem>
                                <DropdownMenuSeparator className="bg-white/10" />
                                <DropdownMenuItem
                                    onClick={async () => {
                                        await logout();
                                        navigate("/");
                                    }}
                                    className="cursor-pointer text-red-400 focus:text-red-300"
                                    data-testid="menu-logout"
                                >
                                    <LogOut size={14} className="mr-2" strokeWidth={1.5} />
                                    Sign Out
                                </DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>
                    ) : (
                        <>
                            <Button
                                variant="ghost"
                                className="hidden sm:inline-flex text-muted-foreground hover:text-foreground"
                                onClick={() => navigate("/auth?mode=login")}
                                data-testid="header-signin-btn"
                            >
                                Sign In
                            </Button>
                            <Button
                                className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-sm tracking-widest uppercase text-xs h-10 px-5"
                                onClick={() => navigate("/services")}
                                data-testid="header-book-btn"
                            >
                                Book Now
                            </Button>
                        </>
                    )}
                </div>
            </div>
            <ServicesMenuBar />
        </header>
    );
}
